import DataView from '@/components/data-view/data-view';
import DocumentEditDrawer from '@/components/document-viewer/document-edit-drawer';
import DocumentViewerButton from '@/components/document-viewer/document-viewer-button';
import DeleteConfirmationModal from '@/components/modal/DeleteConfirmationModal';
import { TableConfig } from '@/components/table/types';
import {
  documentKeys,
  useDeleteDocument,
  useDocuments,
} from '@/hooks/queries/documents';
import {
  DocumentDataType,
  SnapshotRequestType,
  UploadFileDetailType,
} from '@/types/doc';
import { Icon } from '@iconify/react';
import {
  Button,
  Modal,
  ModalBody,
  ModalContent,
  ModalHeader,
  Progress,
  Spinner,
  Tooltip,
} from '@nextui-org/react';
import { useQueryClient } from '@tanstack/react-query';
import axios from 'axios';
import { useCallback, useState } from 'react';
import { useDropzone } from 'react-dropzone';
import toast from 'react-hot-toast';
import { useNavigate, useParams } from 'react-router-dom';

import DocumentService from 'Src/services/document-service';
import SnapshotService from 'Src/services/snapshot-service';
import { useAuthStore } from 'Src/store/use-auth-store';

export default function DocumentsPage() {
  const navigate = useNavigate();
  const { tenantSlug } = useParams<{ tenantSlug: string }>();
  const queryClient = useQueryClient();
  const [isUploadModalOpen, setIsUploadModalOpen] = useState(false);
  const [selectedDocument, setSelectedDocument] =
    useState<DocumentDataType | null>(null);
  const [documentToDelete, setDocumentToDelete] =
    useState<DocumentDataType | null>(null);
  const { data, isLoading: isLoadingDocuments } = useDocuments();
  const orgId = useAuthStore((state) => state.orgId);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);

  const deleteDocument = useDeleteDocument({
    onSuccess: () => setDocumentToDelete(null),
  });

  const constructPostDataForFileUpload = (data: UploadFileDetailType[]) => ({
    batch_id: data[0].batchId,
    org_id: data[0].orgId,
    files: data.map((file) => ({
      document_id: file.docId,
      filename: file.name,
      file_original_name: file.file.name,
      file_mimetype: file.file.type,
    })),
  });

  const upload = async (
    file: File,
    snapshot: SnapshotRequestType,
    onProgress: (progress: number) => void
  ) => {
    const { fields, url } = snapshot;

    const formData = new FormData();
    Object.keys(fields).forEach((key) => {
      formData.append(key, fields[key]);
    });
    formData.append('file', file);

    await axios.post(url, formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
      onUploadProgress: (progressEvent) => {
        onProgress(
          Math.round((progressEvent.loaded * 100) / (progressEvent.total || 1))
        );
      },
    });

    await DocumentService.bulkUploadFinish([snapshot.id]);
    SnapshotService.parseSnapshot(snapshot.id, true);
  };

  const createDocumentSnapshot = async (
    uploadedFiles: UploadFileDetailType[]
  ) => {
    setIsUploading(true);
    setUploadProgress(0);
    try {
      // Call the service directly (not the mutation hook) so the documents
      // list isn't refetched before the files have actually been uploaded.
      const postData = constructPostDataForFileUpload(uploadedFiles);
      const response = (await DocumentService.createDocuments(
        postData as unknown as DocumentDataType
      )) as unknown as {
        documents?: { snapshot?: SnapshotRequestType }[];
      };

      const documents = response?.documents ?? [];
      const toUpload = documents
        .map((doc, index) => ({ doc, file: uploadedFiles[index]?.file }))
        .filter(
          (
            item
          ): item is { doc: { snapshot: SnapshotRequestType }; file: File } =>
            !!item.doc.snapshot?.id && !!item.file
        );

      const progressByFile = toUpload.map(() => 0);
      const results = await Promise.allSettled(
        toUpload.map(({ doc, file }, index) =>
          upload(file, doc.snapshot, (progress) => {
            progressByFile[index] = progress;
            setUploadProgress(
              Math.round(
                progressByFile.reduce((sum, p) => sum + p, 0) /
                  progressByFile.length
              )
            );
          })
        )
      );

      const failedCount = results.filter(
        (result) => result.status === 'rejected'
      ).length;
      if (failedCount) {
        toast.error(
          failedCount === results.length
            ? 'Error uploading file(s)'
            : `${failedCount} of ${results.length} file(s) failed to upload`
        );
      } else {
        toast.success('File(s) uploaded successfully');
        setIsUploadModalOpen(false);
      }
    } catch {
      toast.error('Something went wrong while uploading documents');
    } finally {
      await queryClient.invalidateQueries({ queryKey: documentKeys.lists() });
      setIsUploading(false);
      setUploadProgress(0);
    }
  };

  const tableConfig: TableConfig = {
    columns: [
      {
        uid: 'filename',
        name: 'File Name',
        sortable: true,
      },
      {
        uid: 'created_at',
        name: 'Upload Date',
        sortable: true,
        renderCell: (item: DocumentDataType) => {
          return new Date(item.created_at).toLocaleDateString();
        },
      },
      {
        uid: 'actions',
        name: 'Actions',
        align: 'end',
        width: '160',
        renderCell: (item: DocumentDataType) => {
          return (
            <div className="flex gap-2 justify-end">
              <DocumentViewerButton
                document={item}
                title="View"
                iconName="akar-icons:eye"
                buttonClassName="hover:bg-gray-100 rounded-full transition-colors flex-shrink-0"
                className="hover:text-primary text-xl"
              />
              <Tooltip content="Edit">
                <Button
                  isIconOnly
                  size="sm"
                  variant="light"
                  onPress={() => setSelectedDocument(item)}
                >
                  <Icon icon="akar-icons:edit" width="20" />
                </Button>
              </Tooltip>
              <Tooltip content="Delete">
                <Button
                  isIconOnly
                  size="sm"
                  variant="light"
                  color="danger"
                  onPress={() => setDocumentToDelete(item)}
                >
                  <Icon icon="proicons:delete" width="20" />
                </Button>
              </Tooltip>
            </div>
          );
        },
      },
    ],
    initialVisibleColumns: ['filename', 'created_at', 'actions'],
    searchable: true,
    searchKey: 'filename',
    searchPlaceholder: 'Search documents...',
    bottomContentPlacement: 'outside',
  };

  const onDrop = useCallback(
    (acceptedFiles: File[]) => {
      if (acceptedFiles.length) {
        const fileDetails = acceptedFiles.map((file) => ({
          file,
          batchId: null,
          docId: null,
          name: file.name,
          orgId: orgId,
        }));

        createDocumentSnapshot(fileDetails);
      }
    },
    [orgId]
  );

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    onDropRejected: () =>
      toast.error(
        'Unsupported file type. Please upload PDF, DOC, DOCX or TXT.'
      ),
    accept: {
      'application/pdf': ['.pdf'],
      'application/msword': ['.doc'],
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document':
        ['.docx'],
      'text/plain': ['.txt'],
    },
    disabled: isUploading,
  });

  if (isLoadingDocuments) {
    return (
      <div className="flex h-full min-h-0 flex-1 items-center justify-center">
        <Spinner size="lg" />
      </div>
    );
  }

  return (
    <div className="flex h-full min-h-0 flex-1 flex-col">
      <DataView
        disableTileView
        title="Documents"
        actions={
          <div className="flex gap-2">
            <Button
              variant="bordered"
              startContent={<Icon icon="mdi:reorder-horizontal" />}
              onPress={() => navigate(`/${tenantSlug}/documents/reorder`)}
            >
              Reorder Documents
            </Button>
            <Button
              color="primary"
              startContent={<Icon icon="mdi:upload" />}
              onPress={() => setIsUploadModalOpen(true)}
            >
              Upload Document
            </Button>
          </div>
        }
        data={data?.results || []}
        tableConfig={tableConfig}
        viewType="list"
        onViewChange={() => {}}
        renderCard={() => null}
      />

      {/* Upload Modal */}
      <Modal
        isOpen={isUploadModalOpen}
        onClose={() => setIsUploadModalOpen(false)}
        size="md"
        placement="center"
        isDismissable={!isUploading}
        hideCloseButton={isUploading}
      >
        <ModalContent>
          {() => (
            <>
              <ModalHeader>Upload Document</ModalHeader>
              <ModalBody className="pb-6">
                {/* Fixed height so the drop zone doesn't resize when the drag
                    state changes — resizing moves the border under the cursor
                    and toggles dragenter/dragleave in a loop. */}
                <div
                  {...getRootProps()}
                  className={`flex h-36 flex-col items-center justify-center border-2 border-dashed rounded-lg px-8 text-center transition-colors
                    ${isUploading ? 'cursor-default' : 'cursor-pointer'}
                    ${isDragActive ? 'border-primary bg-primary/10' : 'border-neutral-300'}`}
                >
                  <input {...getInputProps()} />
                  {isUploading ? (
                    <Progress
                      aria-label="Uploading"
                      value={uploadProgress}
                      label={
                        uploadProgress < 100 ? 'Uploading...' : 'Finishing...'
                      }
                      showValueLabel
                      className="max-w-md"
                    />
                  ) : (
                    <>
                      <p>
                        {isDragActive
                          ? 'Drop the files here ...'
                          : "Drag 'n' drop some files here, or click to select files"}
                      </p>
                      <p className="text-sm text-neutral-500 mt-2">
                        Supported files: PDF, DOC, DOCX, TXT
                      </p>
                    </>
                  )}
                </div>
              </ModalBody>
            </>
          )}
        </ModalContent>
      </Modal>

      {/* Document Edit Drawer */}
      {selectedDocument && (
        <DocumentEditDrawer
          isOpen={!!selectedDocument}
          onClose={() => setSelectedDocument(null)}
          document={selectedDocument}
        />
      )}

      <DeleteConfirmationModal
        isOpen={!!documentToDelete}
        onClose={() => setDocumentToDelete(null)}
        onConfirm={() =>
          documentToDelete &&
          deleteDocument.mutate(documentToDelete.id.toString())
        }
        title="Delete Document"
        entityName="document"
        isLoading={deleteDocument.isPending}
      />
    </div>
  );
}
